const contents = `<p>CQS stands for Command Query Separation. It is a technique that consists in keeping separate the code that retrieves data from the code that modifies data.</p>

<p>With CQS, any given function either:</p>

<ul><li>returns data, or</li><li>modifies data</li></ul>

<p>But not both.</p>

<p>The benefit of CQS is to concentrate all the modifications of data in a set of functions that perform side effects, so that the rest of the code is free of side effects. Therefore, CQS is a way to control side effects.</p>

<p>Let’s take an example to illustrate, with a class that computes an average: </p>

<pre><code class="language-ruby">class AverageCalculator
  def with_new_value(new_value)
    @average = @count / (@count + 1) * @average +
                    1 / (@count + 1) * new_value
    @count += 1
    @average
  end
  
  def initialize
    @count = 0.0
    @average = 0
  end
end</code></pre>

<p>A simple way to implement an average is to sum all the values and divide the sum by the number of values. But if the values are big numbers or if there are many of them, the sum can overflow the size of integers.</p>

<p>A way to solve this problem is to compute the average as the weighted average of the current average and the new value. The <code>AverageCalculator</code> class encapsulates this formula on lines 3 and 4.</p>

<p>Here is how the class is used:</p>

<pre><code class="language-ruby">average = AverageCalculator.new

average.with_new_value(10)
average.with_new_value(12)
puts average.with_new_value(20)</code></pre>

<p>This prints:</p>

<pre><code class="language-plaintext">14.0</code></pre>

<p>This class doesn’t respect CQS: the <code>with_new_value</code> method both modifies data (<code>@average</code> and <code>@count</code>) and returns data (<code>@average</code>). Let’s reshape the <code>AverageCalculator</code> class so that it respects CQS.</p>

<h2 id="Separating-command-and-queries">Separating command and queries</h2>

<p>Here is a new version of the class with commands and queries separated:</p>

<pre><code class="language-ruby">class AverageCalculator
  def add_value(new_value) # the command
    @average = @count / (@count + 1) * @average +
                    1 / (@count + 1) * new_value
    @count += 1
    nil
  end

  def result # the query
    @average
  end

  def initialize
    @count = 0.0
    @average = 0
  end
end</code></pre>

<p>Now <code>add_value</code> performs the modifications of data, but doesn’t return anything (Ruby returns whatever is evaluated on the last line of the function, so in a strict coding style we can return <code>nil</code> on the last line).</p>

<p>And <code>result</code> returns some data but doesn’t modify any data.</p>

<p>This changes how the class is used:</p>

<pre><code class="language-ruby">average = AverageCalculator.new

average.add_value(10)
average.add_value(12)
average.add_value(20)

puts average.result</code></pre>

<p>But the code the same result:</p>

<pre><code class="language-plaintext">14.0</code></pre>

<h2 id="Commands-are-allowed-to-return-something">Commands are allowed to return something</h2>

<p>An interesting nuance in CQS is that, even if commands shouldn’t return the data that queries return, it doesn’t mean that they should not return <em>anything</em>.</p>

<p>The fact that queries take care of returning data leaves some room for the commands to return other kinds of things. For example, commands can be chained by returning <code>self</code>:</p>

<pre><code class="language-ruby">class AverageCalculator
  def add_value(new_value)
    @average = @count / (@count + 1) * @average +
                    1 / (@count + 1) * new_value
    @count += 1
    self # &lt;-- add_value now returns self
  end

  def result
    @average
  end

  def initialize
    @count = 0.0
    @average = 0
  end
end</code></pre>

<p>This allows to use the class with chaining:</p>

<pre><code class="language-ruby">average = AverageCalculator.new

average.add_value(10)
       .add_value(12)
       .add_value(20)

puts average.result</code></pre>

<p>The command can leverage on its return value to improve its interface.</p>

<h2 id="CQS-at-different-scales">CQS at different scales</h2>

<p>The example with <code>AverageCalculator</code> illustrates the CQS applied to the functions of a class. But the CQS can also be applied at larger scales.</p>

<p>For example, the commands coming from the UI of an application can be routed to two different kinds of components, depending on whether the UI is asking for a modification of data or for a simply query.</p>

<p>We see that in detail in <a href="/CQRS">CQRS</a>.</p>

<h2 id="Going-further-than-CQS">Going further than CQS</h2>

<p>CQS is a useful pattern to follow as it isolates side effects, reduces coupling and generally makes code easier to reason about. But it’s not a mandatory requirement: if it some cases happens to make your code more cumbersome, it’s ok not to follow it.</p>

<p>CQS concerns output side effects, that is to say the modifications of the data. But CQS doesn’t say anything about <a href="/controlling-side-effects">input side effects</a>, that are the values that functions read but that are not in their parameters.</p>

<p>For example, CQS doesn’t prevent reading data from the database in the queries part of the command. A further useful distinction is to separate the code that access the database from the code that processes the data, even if none of them performs any modifications of the data.</p>

<p>CQS is necessary to understand to be able to understand <a href="/CQRS">CQRS</a>.</p>

<div class="notes">
<p><b>Key points:</b></p>

<ul><li>CQS is about separating code that changes data and code that returns data</li><li>CQS is a way to control side effects</li><li>isolating commands leaves room for other options for their return types</li><li>CQS can be applied at various scales</li><li>CQS doesn’t cover input side effects</li></ul>
</div>`

export default contents;
